import { DocumentHelper } from "./documentHelper";
import { localization } from "../localizationManager";
import { endOfDay, startOfDay } from "./calculationDateRanges";
import { DateRangeModel, DatePeriodEnum, datePeriodsFunctions, IDateRangeModelOptions } from "./dateRangeModel";
import { IDropdownItemOption } from "./dropdownBase";
import { createDropdown } from "./dropdownWidget";

import "./dateRangeWidget.scss";

export type { DatePeriodEnum };
export { datePeriodsFunctions };

function formatLocalDate(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return date.getFullYear() + "-" + month + "-" + day;
}

function localDateFromInput(value: string): Date {
  const parts = value.split("-");
  if(parts.length !== 3) {
    return new Date(NaN);
  }
  return new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
}

export interface IDateRangeWidgetOptions extends IDateRangeModelOptions {
  showAnswerCount?: boolean;
}

export class DateRangeWidget {
  static invalidRangeClassName = "sa-date-range--invalid";
  static invalidRangeEditorClassName = "sa-date-range_editor--invalid";

  private dateRangeContainer: HTMLElement;
  private startDateEditor: HTMLElement;
  private endDateEditor: HTMLElement;
  private startDateInput: HTMLInputElement;
  private endDateInput: HTMLInputElement;
  private countLabel: HTMLElement;
  private rangeErrorMessage: HTMLElement;
  private datePeriodContainer: HTMLElement;
  private includeTodayContainer: HTMLElement;
  private includeTodayCheckbox: HTMLElement;
  private answersCount: number;

  private elementRemoveClassName(element: Element, className: string) {
    if(element.classList.contains(className)) {
      element.classList.remove(className);
    }
  }

  private updateMinMaxAttributes(): void {
    const dateRange = this.model.currentDateRange;
    if(dateRange.end !== undefined) {
      this.startDateInput.max = formatLocalDate(new Date(dateRange.end));
    } else {
      this.startDateInput.removeAttribute("max");
    }

    if(dateRange.start !== undefined) {
      this.endDateInput.min = formatLocalDate(new Date(dateRange.start));
    } else {
      this.endDateInput.removeAttribute("min");
    }
  }

  private setDateIntoInput(dateValue: number, input: HTMLInputElement): void {
    if(!!dateValue) {
      const date = new Date(dateValue);
      input.value = formatLocalDate(date);
    } else {
      input.value = "";
    }
  }

  private createErrorMessage(textContent?: string) {
    const messageDiv = DocumentHelper.createElement("div", "sa-range-error");
    const panelDiv = DocumentHelper.createElement("div", "sa-range-error_panel");
    const errorMessageDiv = DocumentHelper.createElement("div", "sa-range-error_text");
    errorMessageDiv.textContent = textContent;
    this.rangeErrorMessage = errorMessageDiv;

    messageDiv.appendChild(panelDiv);
    panelDiv.appendChild(errorMessageDiv);

    return messageDiv;
  }

  private createDateEditor(dateValue: number, changeHandler: (input: HTMLInputElement) => void, label: string): HTMLElement {
    const dateEditor = DocumentHelper.createElement("div", "sa-date-range_editor");
    const formBox = DocumentHelper.createElement("div", "sa-date-range_editor-formbox");
    const content = DocumentHelper.createElement("div", "sa-date-range_editor-content");
    const input = <HTMLInputElement>DocumentHelper.createElement("input", "sa-date-range_editor-input",
      {
        type: "date"
      }
    );
    input.setAttribute("aria-label", label);
    this.setDateIntoInput(dateValue, input);

    input.onchange = (e) => {
      if(!!changeHandler) {
        changeHandler(input);
      }
    };

    content.appendChild(input);
    formBox.appendChild(content);
    dateEditor.appendChild(formBox);

    return dateEditor;
  }

  private createDivider(): HTMLElement {
    const divider = DocumentHelper.createElement("div", "sa-vertical-divider");
    const line = DocumentHelper.createElement("div", "sa-line");
    divider.appendChild(line);
    return divider;
  }

  private createIncludeTodayCheckbox(): HTMLElement {
    const className = "sa-date-range_include-today";
    const container = DocumentHelper.createElement("div", className);
    const checkbox = DocumentHelper.createElement("div", className + "-checkbox");
    const button = DocumentHelper.createElement("div", className + "-button");
    const check = DocumentHelper.createSvgElement("check-16x16");
    check.classList.add("sa-date-range_include-today-check");
    button.appendChild(check);

    const caption = DocumentHelper.createElement("div", className + "-caption");
    const captionText = DocumentHelper.createElement("div", className + "-caption-text");
    captionText.textContent = localization.getString("includeToday");
    caption.appendChild(captionText);
    checkbox.appendChild(button);
    checkbox.appendChild(caption);
    container.appendChild(checkbox);

    const toggleCheckbox = () => {
      this.model.toggleIncludeToday();
      this.updateElements();
    };
    button.onclick = toggleCheckbox;

    this.includeTodayCheckbox = button;
    return container;
  }

  private updateIncludeTodayCheckbox() {
    if(this.includeTodayContainer && this.includeTodayCheckbox) {
      const className = "sa-date-range_include-today-checkbox--checked";
      this.includeTodayContainer.style.display = this.model.periodIsToDate() ? "" : "none";
      if(this.model.includeToday) {
        this.includeTodayCheckbox.classList.add(className);
      } else {
        this.includeTodayCheckbox.classList.remove(className);
      }
    }
  }

  constructor(private model: DateRangeModel, private options: IDateRangeWidgetOptions) {
  }

  public render(): HTMLElement {
    const rangeElement = DocumentHelper.createElement("div", "sa-date-range");
    const rangeContainer = DocumentHelper.createElement("div", "sa-date-range-container");
    const dateRangeEditors = DocumentHelper.createElement("div", "sa-date-range_editors");
    this.dateRangeContainer = rangeContainer;
    rangeContainer.appendChild(dateRangeEditors);
    rangeElement.appendChild(rangeContainer);

    const currentDateRange = this.model.currentDateRange;

    this.startDateEditor = this.createDateEditor(currentDateRange.start, (input: HTMLInputElement) => {
      if(input.checkValidity()) {
        this.model.setFilter(startOfDay(localDateFromInput(input.value)).getTime(), this.model.currentDateRange.end, true);
        this.updateElements();
      } else {
        this.startDateEditor.classList.add(DateRangeWidget.invalidRangeEditorClassName);
        this.dateRangeContainer.classList.add(DateRangeWidget.invalidRangeClassName);
        this.rangeErrorMessage.textContent = input.validationMessage;
      }
    }, localization.getString("startDate"));
    dateRangeEditors.appendChild(this.startDateEditor);
    this.startDateInput = this.startDateEditor.querySelector("input");
    this.setDateIntoInput(currentDateRange.start, this.startDateInput);

    const separator = DocumentHelper.createElement("div", "");
    separator.textContent = "-";
    dateRangeEditors.appendChild(separator);

    this.endDateEditor = this.createDateEditor(currentDateRange.end, (input: HTMLInputElement) => {
      if(input.checkValidity()) {
        this.model.setFilter(this.model.currentDateRange.start, endOfDay(localDateFromInput(input.value)).getTime(), true);
        this.updateElements();
      } else {
        this.endDateEditor.classList.add(DateRangeWidget.invalidRangeEditorClassName);
        this.dateRangeContainer.classList.add(DateRangeWidget.invalidRangeClassName);
        this.rangeErrorMessage.textContent = input.validationMessage;
      }
    }, localization.getString("endDate"));
    dateRangeEditors.appendChild(this.endDateEditor);
    rangeContainer.appendChild(this.createErrorMessage());

    this.endDateInput = this.endDateEditor.querySelector("input");
    this.setDateIntoInput(currentDateRange.end, this.endDateInput);

    if(this.model.availableDatePeriods.length > 0) {
      const options = [];
      this.model.availableDatePeriods.forEach(key => {
        options.push(<IDropdownItemOption>{
          value: key,
          text: localization.getString("reportingPeriod" + key.charAt(0).toUpperCase() + key.slice(1))
        });
      });

      this.datePeriodContainer = createDropdown({
        options,
        isSelected: (option: any) => this.model.currentDatePeriod === option.value,
        handler: (value: any) => {
          this.setDatePeriod(value);
        },
        resetHandler: () => {
          this.setDatePeriod(undefined);
        },
        placeholder: localization.getString("selectDateRange"),
        size: "small"
      });
      this.datePeriodContainer.classList.add("sa-date-range_dropdown");

      rangeElement.appendChild(this.createDivider());
      rangeElement.appendChild(this.datePeriodContainer);

      this.includeTodayContainer = this.createIncludeTodayCheckbox();
      this.updateIncludeTodayCheckbox();
      rangeElement.appendChild(this.includeTodayContainer);
    }

    if(this.options.showAnswerCount !== false) {
      rangeElement.appendChild(this.createDivider());
      const countContainer = DocumentHelper.createElement("div", "sa-count");
      this.countLabel = DocumentHelper.createElement("div", "sa-count_text");
      this.updateAnswersCount();
      countContainer.appendChild(this.countLabel);
      rangeElement.appendChild(countContainer);
    }

    return rangeElement;
  }

  public setDatePeriod(datePeriod: DatePeriodEnum | undefined): void {
    this.model.setDatePeriod(datePeriod);
    this.updateElements();
  }

  public updateElements(): void {
    if(this.datePeriodContainer) {
      this.datePeriodContainer["__updateHeader"] && this.datePeriodContainer["__updateHeader"]();
    }
    this.updateMinMaxAttributes();
    const range = this.model.currentDateRange;
    this.setDateIntoInput(range.start, this.startDateInput);
    this.setDateIntoInput(range.end, this.endDateInput);
    this.elementRemoveClassName(this.startDateEditor, DateRangeWidget.invalidRangeEditorClassName);
    this.elementRemoveClassName(this.endDateEditor, DateRangeWidget.invalidRangeEditorClassName);
    this.updateIncludeTodayCheckbox();
  }

  public updateAnswersCount(answersCount?: number): void {
    if(answersCount !== undefined && answersCount !== null) {
      this.answersCount = answersCount;
    }
    if(!!this.countLabel && this.answersCount !== undefined) {
      this.countLabel.textContent = this.answersCount + " " + localization.getString("responsesText");
    }
  }

  public destroy() {
    this.dateRangeContainer.innerHTML = "";
    this.dateRangeContainer = undefined;
    this.startDateEditor.innerHTML = "";
    this.startDateEditor = undefined;
    this.endDateEditor.innerHTML = "";
    this.endDateEditor = undefined;
    this.startDateInput.innerHTML = "";
    this.startDateInput = undefined;
    this.endDateInput.innerHTML = "";
    this.endDateInput = undefined;
    this.countLabel.innerHTML = "";
    this.countLabel = undefined;
    this.rangeErrorMessage.innerHTML = "";
    this.rangeErrorMessage = undefined;
    this.datePeriodContainer.innerHTML = "";
    this.datePeriodContainer = undefined;
    this.includeTodayContainer = undefined;
    this.includeTodayCheckbox = undefined;
  }
}
